"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="236431" className="hidden md:flex gap-6">
        <div className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 transition-colors hover:text-foreground relative after:absolute after:-bottom-1.5 after:left-0 after:h-[1px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  setOpen
}: {
  setOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-6 w-32 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-6 w-40 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-6 w-24 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-12">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-xl font-default font-medium text-foreground/90 transition-colors hover:text-primary" onClick={() => setOpen(false)} data-index={i}>
          {item.label}
        </Link>)}
      <div className="mt-8 pt-8 border-t border-border/20">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-transform duration-200 hover:scale-[1.02] h-12 text-base font-default" asChild>
          <Link to="#" onClick={() => setOpen(false)}>
            Gratis Demo
          </Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <header className={`w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20" : "bg-background border-b border-transparent"}`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex-shrink-0 z-50">
            <Link to="/" className="flex items-center">
              <img src="https://social-wallaby.10web.cloud/wp-content/uploads/2026/04/X.png" alt="Xora" className="h-20 w-auto object-contain" />
            </Link>
          </div>

          <div className="flex-1 flex justify-center">
            <MenuProvider menu_id="41">
              <DesktopNav />
            </MenuProvider>
          </div>

          <div className="flex-shrink-0 flex items-center justify-end gap-4">
            <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-xl shadow-primary/20 font-default font-semibold px-8 animate-pulse hover:animate-none" asChild>
              <Link to="https://xora.no/xora-demo/">Gratis Demo</Link>
            </Button>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-white/5" aria-label="Toggle Menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] sm:w-[400px] bg-background/95 backdrop-blur-2xl border-l border-white/10 p-6">
                <SheetTitle className="sr-only">Navigasjonsmeny</SheetTitle>
                <MenuProvider menu_id="41">
                  <MobileNav setOpen={setMobileMenuOpen} />
                </MenuProvider>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </section>;
}